import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';

interface VarianceAnalysisScreenThumbnailProps {
  isHovered?: boolean;
  alwaysVisible?: boolean;
}

export default function VarianceAnalysisScreenThumbnail({ 
  isHovered = false, 
  alwaysVisible = false 
}: VarianceAnalysisScreenThumbnailProps) {
  const baseScale = alwaysVisible ? 'scale-[0.4]' : 'scale-100';
  const hoverScale = isHovered ? 'scale-100' : baseScale;

  return (
    <div className={`transition-transform duration-300 origin-left ${hoverScale} ${isHovered ? 'z-50' : ''}`}>
      <Card className="w-[300px] shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">Variance Analysis</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="p-2 rounded bg-muted/50">
                <div className="text-muted-foreground">Weekly</div>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendingDown className="h-3 w-3 text-red-500" />
                  <span className="font-semibold text-red-500">-6.7%</span>
                </div>
              </div>
              <div className="p-2 rounded bg-muted/50">
                <div className="text-muted-foreground">Monthly</div>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendingUp className="h-3 w-3 text-green-500" />
                  <span className="font-semibold text-green-500">+2.3%</span>
                </div>
              </div>
              <div className="p-2 rounded bg-muted/50">
                <div className="text-muted-foreground">Quarterly</div>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendingUp className="h-3 w-3 text-green-500" />
                  <span className="font-semibold text-green-500">+4.1%</span>
                </div>
              </div>
            </div>
            <div className="h-20 flex items-end gap-1">
              {[
                { forecast: 80, actual: 75 },
                { forecast: 85, actual: 88 },
                { forecast: 90, actual: 92 },
                { forecast: 75, actual: 70 },
                { forecast: 95, actual: 98 },
                { forecast: 88, actual: 85 },
              ].map((data, i) => (
                <div key={i} className="flex-1 flex gap-0.5">
                  <div className="flex-1 bg-blue-300 rounded-t" style={{ height: `${data.forecast}%` }} />
                  <div className="flex-1 bg-blue-600 rounded-t" style={{ height: `${data.actual}%` }} />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-300 rounded" />
                <span>Forecast</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-600 rounded" />
                <span>Actual</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
